// src/services/AdminService.js
import { ref, onValue, set, get, update } from 'firebase/database';
import { database } from '../firebase';
import StripeAdminService from './StripeAdminService';

class AdminService {
  /**
   * Get all bookings from both user and guest bookings with Stripe data
   * @returns {Promise<Array>} - Array of all bookings with source type and Stripe data
   */
  static async getAllBookings() {
    try {
      console.log('📡 AdminService: Starting getAllBookings...');
      const allBookings = [];

      // Get user bookings
      console.log('👥 AdminService: Fetching users data...');
      const usersRef = ref(database, 'users');
      const usersSnapshot = await get(usersRef);
      console.log('👥 AdminService: Users snapshot exists:', usersSnapshot.exists());

      if (usersSnapshot.exists()) {
        const usersData = usersSnapshot.val();

        Object.entries(usersData).forEach(([userId, userData]) => {
          if (userData.bookings) {
            Object.entries(userData.bookings).forEach(([bookingId, booking]) => {
              allBookings.push({
                ...booking,
                bookingId,
                userId,
                userEmail: userData.email || 'N/A',
                userName: userData.displayName || userData.firstName || 'N/A',
                bookingType: 'user',
                userInfo: {
                  email: userData.email,
                  displayName: userData.displayName,
                  firstName: userData.firstName,
                  lastName: userData.lastName,
                  phone: userData.phone
                }
              });
            });
          }
        });
      }

      // Get guest bookings
      const guestBookingsRef = ref(database, 'guestBookings');
      const guestSnapshot = await get(guestBookingsRef);

      if (guestSnapshot.exists()) {
        const guestData = guestSnapshot.val();

        Object.entries(guestData).forEach(([bookingId, booking]) => {
          allBookings.push({
            ...booking,
            bookingId,
            userId: null,
            userEmail: booking.guestEmail || 'N/A',
            userName: 'Guest User',
            bookingType: 'guest',
            userInfo: {
              email: booking.guestEmail,
              phone: booking.guestPhone,
              displayName: 'Guest User'
            }
          });
        });
      }

      // Enrich with Stripe payment data
      const enrichedBookings = await StripeAdminService.enrichBookingsWithStripeData(allBookings);

      // Sort by creation date (newest first)
      enrichedBookings.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.schedule?.date);
        const dateB = new Date(b.createdAt || b.schedule?.date);
        return dateB - dateA;
      });

      return enrichedBookings;
    } catch (error) {
      console.error('Error fetching all bookings:', error);
      throw new Error('Failed to fetch bookings');
    }
  }

  /**
   * Listen to all bookings in real-time
   * @param {function} callback - Callback function to receive updates
   * @returns {function} - Unsubscribe function
   */
  static subscribeToAllBookings(callback) {
    const listeners = [];
    let userBookings = {};
    let guestBookings = {};

    const updateBookings = () => {
      const allBookings = [];

      // Process user bookings
      Object.entries(userBookings).forEach(([userId, userData]) => {
        if (userData.bookings) {
          Object.entries(userData.bookings).forEach(([bookingId, booking]) => {
            allBookings.push({
              ...booking,
              bookingId,
              userId,
              userEmail: userData.email || 'N/A',
              userName: userData.displayName || userData.firstName || 'N/A',
              bookingType: 'user',
              userInfo: {
                email: userData.email,
                displayName: userData.displayName,
                firstName: userData.firstName,
                lastName: userData.lastName,
                phone: userData.phone
              }
            });
          });
        }
      });

      // Process guest bookings
      Object.entries(guestBookings).forEach(([bookingId, booking]) => {
        allBookings.push({
          ...booking,
          bookingId,
          userId: null,
          userEmail: booking.guestEmail || 'N/A',
          userName: 'Guest User',
          bookingType: 'guest',
          userInfo: {
            email: booking.guestEmail,
            phone: booking.guestPhone,
            displayName: 'Guest User'
          }
        });
      });

      // Sort by creation date (newest first)
      allBookings.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.schedule?.date);
        const dateB = new Date(b.createdAt || b.schedule?.date);
        return dateB - dateA;
      });

      callback(allBookings);
    };

    // Listen to user bookings
    const usersRef = ref(database, 'users');
    const usersListener = onValue(usersRef, (snapshot) => {
      userBookings = snapshot.val() || {};
      updateBookings();
    });
    listeners.push(usersListener);

    // Listen to guest bookings
    const guestBookingsRef = ref(database, 'guestBookings');
    const guestListener = onValue(guestBookingsRef, (snapshot) => {
      guestBookings = snapshot.val() || {};
      updateBookings();
    });
    listeners.push(guestListener);

    // Return unsubscribe function
    return () => {
      listeners.forEach(listener => listener());
    };
  }

  /**
   * Update booking status (works for both user and guest bookings)
   * @param {Object} booking - The booking object
   * @param {string} newStatus - The new status
   * @returns {Promise<void>}
   */
  static async updateBookingStatus(booking, newStatus) {
    try {
      let bookingRef;

      if (booking.bookingType === 'user' && booking.userId) {
        bookingRef = ref(database, `users/${booking.userId}/bookings/${booking.bookingId}/status`);
      } else if (booking.bookingType === 'guest') {
        bookingRef = ref(database, `guestBookings/${booking.bookingId}/status`);
      } else {
        throw new Error('Invalid booking type or missing user ID');
      }

      await set(bookingRef, newStatus);
    } catch (error) {
      console.error('Error updating booking status:', error);
      throw new Error('Failed to update booking status');
    }
  }

  /**
   * Add notes to a booking (works for both user and guest bookings)
   * @param {Object} booking - The booking object
   * @param {string} notes - The notes to add
   * @returns {Promise<void>}
   */
  static async updateBookingNotes(booking, notes) {
    try {
      let bookingRef;

      if (booking.bookingType === 'user' && booking.userId) {
        bookingRef = ref(database, `users/${booking.userId}/bookings/${booking.bookingId}/notes`);
      } else if (booking.bookingType === 'guest') {
        bookingRef = ref(database, `guestBookings/${booking.bookingId}/notes`);
      } else {
        throw new Error('Invalid booking type or missing user ID');
      }

      await set(bookingRef, notes);
    } catch (error) {
      console.error('Error updating booking notes:', error);
      throw new Error('Failed to update booking notes');
    }
  }

  /**
   * Get user permission level
   * @param {string} userId - User ID to check
   * @returns {Promise<Object>} - User permissions
   */
  static async getUserPermissions(userId) {
    try {
      const userRef = ref(database, `users/${userId}`);
      const snapshot = await get(userRef);

      if (snapshot.exists()) {
        const userData = snapshot.val();
        return {
          role: userData.role || 'user',
          permissions: userData.permissions || {},
          isAdmin: userData.role === 'admin' || userData.permissions?.admin === true
        };
      }

      return { role: 'user', permissions: {}, isAdmin: false };
    } catch (error) {
      console.error('Error getting user permissions:', error);
      throw new Error('Failed to get user permissions');
    }
  }

  /**
   * Elevate user to admin
   * @param {string} userId - User ID to elevate
   * @returns {Promise<void>}
   */
  static async elevateUserToAdmin(userId) {
    try {
      await set(ref(database, `users/${userId}/role`), 'admin');

      await set(ref(database, `users/${userId}/permissions`), {
        admin: true,
        elevated_at: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error elevating user to admin:', error);
      throw new Error('Failed to elevate user permissions');
    }
  }

  /**
   * Get all users for admin management with Stripe customer data
   * @returns {Promise<Array>} - Array of all users enriched with Stripe data
   */
  static async getAllUsers() {
    try {
      console.log('👥 AdminService: Fetching users from Firebase...');
      const usersRef = ref(database, 'users');
      const snapshot = await get(usersRef);

      if (snapshot.exists()) {
        const usersData = snapshot.val();
        const users = Object.entries(usersData).map(([userId, userData]) => ({
          userId,
          ...userData,
          isAdmin: userData.role === 'admin' || userData.permissions?.admin === true
        }));

        console.log(`👥 AdminService: Found ${users.length} users, linking with Stripe...`);

        // Link users with their Stripe customer data
        const enrichedUsers = await StripeAdminService.linkUsersWithStripeCustomers(users);

        console.log(`✅ AdminService: Successfully enriched users with Stripe data`);
        return enrichedUsers;
      }

      return [];
    } catch (error) {
      console.error('Error getting all users:', error);
      throw new Error('Failed to get users');
    }
  }

  // ── Availability / Blocked Dates & Times ──

  /**
   * Block an entire day or specific time slots.
   * Data stored at `availability/blocked/<YYYY-MM-DD>`.
   *
   * @param {string} dateKey  - e.g. "2026-05-01"
   * @param {Object} blockData - { allDay: true } or { times: ['9:00 AM','10:00 AM'] }
   */
  static async setBlockedDate(dateKey, blockData) {
    try {
      const blockRef = ref(database, `availability/blocked/${dateKey}`);
      await set(blockRef, blockData);
    } catch (error) {
      console.error('Error setting blocked date:', error);
      throw new Error('Failed to update availability');
    }
  }

  /**
   * Remove all blocks for a given date.
   * @param {string} dateKey - e.g. "2026-05-01"
   */
  static async unblockDate(dateKey) {
    try {
      const blockRef = ref(database, `availability/blocked/${dateKey}`);
      await set(blockRef, null);
    } catch (error) {
      console.error('Error unblocking date:', error);
      throw new Error('Failed to update availability');
    }
  }

  /**
   * Get all blocked dates (one-time fetch).
   * @returns {Promise<Object>} - { "2026-05-01": { allDay: true }, ... }
   */
  static async getBlockedDates() {
    try {
      const blockedRef = ref(database, 'availability/blocked');
      const snapshot = await get(blockedRef);
      return snapshot.exists() ? snapshot.val() : {};
    } catch (error) {
      console.error('Error fetching blocked dates:', error);
      return {};
    }
  }

  /**
   * Subscribe to blocked dates in real-time.
   * @param {function} callback - receives the blocked-dates object
   * @returns {function} unsubscribe
   */
  static subscribeToBlockedDates(callback) {
    const blockedRef = ref(database, 'availability/blocked');
    return onValue(blockedRef, (snapshot) => {
      callback(snapshot.exists() ? snapshot.val() : {});
    });
  }

  /**
   * Get comprehensive dashboard statistics with real data
   * @returns {Promise<Object>} - Dashboard statistics including Stripe data
   */
  static async getDashboardStatistics() {
    try {
      // Get basic data
      const [users, bookings] = await Promise.all([
        this.getAllUsers(),
        this.getAllBookings()
      ]);

      // Calculate basic stats
      const totalUsers = users.length;
      const adminUsers = users.filter(u => u.isAdmin).length;
      const totalBookings = bookings.length;

      // Calculate booking statistics
      const pendingBookings = bookings.filter(b => !b.status || b.status === 'pending').length;
      const confirmedBookings = bookings.filter(b => b.status === 'confirmed').length;
      const completedBookings = bookings.filter(b => b.status === 'completed').length;
      const cancelledBookings = bookings.filter(b => b.status === 'cancelled').length;

      // Calculate date-based statistics
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);

      const thisWeek = new Date(today);
      thisWeek.setDate(today.getDate() - today.getDay());

      const thisMonth = new Date(today.getFullYear(), today.getMonth(), 1);

      const todayBookings = bookings.filter(b => {
        const bookingDate = new Date(b.schedule?.date);
        return bookingDate >= today && bookingDate < tomorrow;
      }).length;

      const thisWeekBookings = bookings.filter(b => {
        const bookingDate = new Date(b.schedule?.date);
        return bookingDate >= thisWeek;
      }).length;

      const thisMonthBookings = bookings.filter(b => {
        const bookingDate = new Date(b.schedule?.date);
        return bookingDate >= thisMonth;
      }).length;

      // Calculate revenue from booking data
      const totalRevenue = bookings.reduce((sum, booking) => {
        return sum + (booking.payment?.total || booking.service?.price || 0);
      }, 0);

      const thisMonthRevenue = bookings
        .filter(b => {
          const bookingDate = new Date(b.schedule?.date);
          return bookingDate >= thisMonth;
        })
        .reduce((sum, booking) => {
          return sum + (booking.payment?.total || booking.service?.price || 0);
        }, 0);

      // Get comprehensive Stripe revenue analysis
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      const stripeAnalysis = await StripeAdminService.getRevenueAnalysis(
        thirtyDaysAgo.toISOString().split('T')[0],
        new Date().toISOString().split('T')[0]
      );

      // Calculate Stripe-verified revenue from actual payments
      const stripeVerifiedRevenue = bookings.reduce((sum, booking) => {
        if (booking.stripePayment && booking.payment?.stripeStatus === 'succeeded') {
          return sum + (booking.payment.stripeNetAmount || booking.payment.stripeAmount || 0);
        }
        return sum;
      }, 0);

      // Recent bookings for dashboard
      const recentBookings = bookings
        .sort((a, b) => new Date(b.createdAt || b.schedule?.date) - new Date(a.createdAt || a.schedule?.date))
        .slice(0, 10);

      return {
        users: {
          total: totalUsers,
          admins: adminUsers,
          regular: totalUsers - adminUsers
        },
        bookings: {
          total: totalBookings,
          pending: pendingBookings,
          confirmed: confirmedBookings,
          completed: completedBookings,
          cancelled: cancelledBookings,
          today: todayBookings,
          thisWeek: thisWeekBookings,
          thisMonth: thisMonthBookings
        },
        revenue: {
          total: totalRevenue,
          thisMonth: thisMonthRevenue,
          average: totalBookings > 0 ? totalRevenue / totalBookings : 0,
          stripe: {
            ...stripeAnalysis,
            verifiedRevenue: stripeVerifiedRevenue,
            lastThirtyDays: stripeAnalysis.revenue
          }
        },
        recentBookings,
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error getting dashboard statistics:', error);
      throw new Error('Failed to get dashboard statistics');
    }
  }
}

export default AdminService;